import React, { useState, useEffect, useContext } from 'react';
import { SearchIntailSection, SectionHeader, SectionTitle, ResetButton, HistoryItem } from './styles';
import { useExchangeStore } from '@/app/hooks/trade/exchangeStore';
import { useCopyPortfolioStore } from '@/app/hooks/copyTrading/copyPortfolioStore';
import HeaderSearchItem from '../HeaderSearchLists/HeaderSearchItem';
import { IconTrash } from '@tabler/icons-react';
import { ThemeContext } from '@/theme/ThemeProvider';
import { useDemoTradingStore } from '@/app/hooks/trade/demoTradingStore';

const HISTORY_KEY = 'search_history';

const SearchIntialRender = () => {
  const { theme } = useContext(ThemeContext);
  const { EPs } = useExchangeStore();
  const { portfolios, fetchPortfolios, isLoading } = useCopyPortfolioStore();
  const [searchHistory, setSearchHistory] = useState<any[]>([]);
  const { isDemoMode } = useDemoTradingStore();

  // localStorage에서 히스토리 로드
  useEffect(() => {
    const saved = localStorage.getItem(HISTORY_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setSearchHistory(parsed.slice(0, 3)); // 최근 3개만
      } catch (e) {
        console.error('Failed to parse search history', e);
      }
    }
  }, []);

  // Copy Trading 포트폴리오 로드
  useEffect(() => {
    if (!portfolios || portfolios.length === 0) {
      fetchPortfolios({
        sortBy: 'totalPnl',
        order: 'desc',
        status: 1,
        page: 1,
        limit: 1,
      });
    }
  }, []);

  // 히스토리 초기화
  const handleResetHistory = () => {
    localStorage.removeItem(HISTORY_KEY);
    setSearchHistory([]);
  };

  // Top Searched - 상위 5개 (임시로 처음 5개)
  const topSearched = EPs?.slice(0, 5) || [];

  // Top 1 Master Trader - API 데이터 사용
  const topTrader = portfolios && portfolios.length > 0 ? {
    name: portfolios[0].portfolioName || 'Unknown',
    subtitle: portfolios[0].leaderUsername || '',
    image: portfolios[0].metadata?.imageUrl || null,
    followers: portfolios[0].currentFollowers || 0,
    maxFollowers: portfolios[0].maxFollowers || 0,
    winRate: 0, // API에 없음
    roi: parseFloat(portfolios[0].totalPnl?.toString() || '0'),
    portfolioId: portfolios[0].id,
  } : null;

  return (
    <SearchIntailSection>
      {/* Search History Section */}
      {searchHistory.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <SectionHeader>
            <SectionTitle>History</SectionTitle>
            <ResetButton onClick={handleResetHistory}>
              <IconTrash size={14} />
              Reset
            </ResetButton>
          </SectionHeader>
          {searchHistory.map((item, index) => (
            <HistoryItem
              key={index}
              onClick={() => {
                // isDemoMode 상태로 demo/real 구분
                const basePath = isDemoMode ? '/demo-trade' : '/trade';
                window.location.href = `${basePath}/${item.id}`;
              }}
            >
              <div style={{ fontSize: 11, opacity: 0.5, color: theme.COLORS.textw }}>
                {item.type || 'USDT Perpetual'}
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, marginTop: 2, color: theme.COLORS.textw }}>
                {item.pairs}
              </div>
            </HistoryItem>
          ))}
        </div>
      )}

      {/* Top Searched Section */}
      <SectionHeader>
        <SectionTitle>Top Searched</SectionTitle>
      </SectionHeader>

      <div style={{ marginBottom: 24 }}>
        {topSearched.map((item, index) => (
          <HeaderSearchItem key={item.id} item={item} index={index} />
        ))}
      </div>

      {/* Top Master Traders Section */}
      <SectionHeader style={{ marginTop: 24 }}>
        <SectionTitle>Top 1 Master Traders</SectionTitle>
        <span
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: '#007aff',
            cursor: 'pointer',
          }}
          onClick={() => {
            window.location.href = '/copy-trade';
          }}
        >
          View All →
        </span>
      </SectionHeader>

      {topTrader ? (
        <div
          style={{
            padding: '12px',
            borderRadius: 8,
            background: 'rgba(255, 255, 255, 0.03)',
            cursor: 'pointer',
          }}
          onClick={() => {
            window.location.href = '/copy-trade';
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: topTrader.image
                  ? `url(${topTrader.image}) center/cover`
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 20,
                fontWeight: 700,
                color: '#fff',
              }}
            >
              {!topTrader.image && (topTrader.name?.[0] || 'T')}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: theme.COLORS.textw }}>{topTrader.name}</span>
                {topTrader.subtitle && (
                  <span style={{ fontSize: 12, opacity: 0.6, color: theme.COLORS.textw }}>{topTrader.subtitle}</span>
                )}
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: 16,
                  marginTop: 8,
                  fontSize: 11,
                  opacity: 0.7,
                  color: theme.COLORS.textw,
                }}
              >
                <div>
                  <div>Followers</div>
                  <div style={{ fontWeight: 600, marginTop: 2 }}>
                    {topTrader.followers}/{topTrader.maxFollowers}
                  </div>
                </div>
                <div>
                  <div>Total PnL</div>
                  <div
                    style={{
                      fontWeight: 600,
                      marginTop: 2,
                      color: topTrader.roi > 0 ? '#26a69a' : topTrader.roi < 0 ? '#f44336' : '#999',
                    }}
                  >
                    {topTrader.roi > 0 ? '+' : ''}{topTrader.roi.toFixed(2)} USDT
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ padding: 16, fontSize: 13, opacity: 0.5, textAlign: 'center' }}>
          {isLoading ? 'Loading...' : 'No traders available'}
        </div>
      )}
    </SearchIntailSection>
  );
};

export default SearchIntialRender;
